import { createPortal } from 'react-dom';
import { FormProvider } from 'react-hook-form';

import { PaymentsExport } from '~entities/payment/ui/payments-export/payments-export';

import { useIsDesktop } from '~shared/lib/responsive';
import { DataTable } from '~shared/ui/data-table';
import { PeriodRangeFilter } from '~shared/ui/period-range-filter';
import { TableColumnsButton } from '~shared/ui/table-columns-button';
import { TableSortButton } from '~shared/ui/table-sort-button';

import { PaymentsFilters } from './filters/payments-filters';
import { PaymentsFiltersMobile } from './filters/payments-filters-mobile';

import { COLUMN_OPTIONS, SORT_OPTIONS, columns } from '../lib/columns';
import {
  PERIOD_MAX_DATE,
  PERIOD_MIN_DATE,
  PERIOD_PRESETS,
} from '../lib/period-options';
import { DEFAULT_SORTING, TABLE_MIN_WIDTH } from '../lib/table-options';
import type { PaymentsFiltersValue } from '../model/payments-filters';
import { usePaymentsTable } from '../model/use-payments-table';

type Props = {
  headerSlot?: HTMLElement | null;
};

export const PaymentsTable = ({ headerSlot }: Props) => {
  const {
    methods,
    payments,
    total,
    isLoading,
    pagination,
    sorting,
    searchParams,
    hideMerchantFilter,
    applyFilters,
    resetFilters,
    clearSelectFilters,
    activeFiltersCount,
    onSortingChange,
    visibleColumns,
    onVisibleColumnsChange,
    columnVisibility,
    onPageChange,
    onPageSizeChange,
  } = usePaymentsTable();

  const isDesktop = useIsDesktop();

  const periodFilter = (
    <PeriodRangeFilter<PaymentsFiltersValue>
      name="period"
      presets={PERIOD_PRESETS}
      minDate={PERIOD_MIN_DATE}
      maxDate={PERIOD_MAX_DATE}
      onChange={applyFilters}
    />
  );

  const headerControls = (
    <>
      {periodFilter}
      <PaymentsExport params={searchParams} disabled={payments.length === 0} />
    </>
  );

  const responsiveProps = isDesktop
    ? { minWidth: TABLE_MIN_WIDTH, sorting, onSortingChange }
    : { minWidth: 0, columnVisibility };

  return (
    <FormProvider {...methods}>
      {isDesktop &&
        (headerSlot ? (
          createPortal(headerControls, headerSlot)
        ) : (
          <div className="flex flex-wrap items-center justify-end gap-2">
            {headerControls}
          </div>
        ))}

      <div
        className={`
          flex flex-col gap-4
          lg:gap-6
        `}
      >
        {isDesktop ? (
          <PaymentsFilters
            hideMerchantFilter={hideMerchantFilter}
            onFiltersChange={applyFilters}
            onReset={resetFilters}
          />
        ) : (
          <div
            className={`
              -mx-4 flex
              [scrollbar-width:none]
              gap-2 overflow-x-auto px-4
              *:shrink-0
              [&::-webkit-scrollbar]:hidden
            `}
          >
            {periodFilter}

            <TableSortButton
              options={SORT_OPTIONS}
              value={sorting ?? DEFAULT_SORTING}
              onApply={onSortingChange}
            />

            <TableColumnsButton
              options={COLUMN_OPTIONS}
              value={visibleColumns}
              onApply={onVisibleColumnsChange}
            />

            <PaymentsExport
              params={searchParams}
              disabled={payments.length === 0}
              size="s"
            />
          </div>
        )}

        <DataTable
          data={payments}
          columns={columns}
          isLoading={isLoading}
          emptyState="We didn't find anything"
          pagination={
            total > 0
              ? {
                  page: pagination.page,
                  pageSize: pagination.perPage,
                  itemsTotal: total,
                  onPageChange,
                  onPageSizeChange,
                }
              : undefined
          }
          {...responsiveProps}
        />
      </div>

      {!isDesktop && (
        <PaymentsFiltersMobile
          hideMerchantFilter={hideMerchantFilter}
          activeCount={activeFiltersCount}
          onApply={applyFilters}
          onClear={clearSelectFilters}
        />
      )}
    </FormProvider>
  );
};
